import { createTheme } from '@material-ui/core/styles';

/**
 * Hybrid enterprise finance portal tokens — blends patterns from
 * Salesforce (primary blue, list views), NetSuite (dense data, warm accent),
 * Workday (soft canvas, teal accent), Stripe (card shadows, polish),
 * and SAP Fiori (semantic status colors, clear hierarchy).
 */
export const FINANCE = {
  primary: '#0176D3',
  primaryDark: '#014486',
  primaryLight: '#EEF4FC',
  secondary: '#E87722',
  accent: '#087990',
  success: '#2E844A',
  warning: '#FFB75D',
  error: '#C23934',

  text: '#181818',
  ink: '#3e3e3c',
  gray: '#444444',
  muted: '#706e6b',
  line: '#e5e7eb',
  lineDark: '#d1d5db',
  surface: '#ffffff',
  canvas: '#f4f6f9',
  wash: '#f9fafb',
  navBg: '#ffffff',
  navActiveBg: '#EEF4FC',
  tableHead: '#f3f4f6',

  shadowSm: '0 1px 2px rgba(16, 24, 40, 0.06)',
  shadowMd: '0 4px 16px rgba(16, 24, 40, 0.08)',
  shadowLg: '0 8px 24px rgba(16, 24, 40, 0.12)',
  radius: 8,
  radiusSm: 4,
  radiusLg: 12,

  /** @deprecated Prefer FINANCE.text or FINANCE.primary */
  black: '#181818',
} as const;

export const financeMuiTheme = createTheme({
  palette: {
    type: 'light',
    primary: { main: FINANCE.primary, dark: FINANCE.primaryDark, contrastText: '#fff' },
    secondary: { main: FINANCE.secondary },
    success: { main: FINANCE.success },
    warning: { main: FINANCE.warning },
    error: { main: FINANCE.error },
    background: { default: FINANCE.canvas, paper: FINANCE.surface },
    text: { primary: FINANCE.text, secondary: FINANCE.gray },
    divider: FINANCE.line,
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.02em', color: FINANCE.text },
    h2: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em' },
    h3: { fontWeight: 600, fontSize: '1.1rem' },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.45 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: FINANCE.radius },
  overrides: {
    MuiButton: {
      root: { borderRadius: FINANCE.radiusSm, padding: '8px 16px', boxShadow: 'none' },
      containedPrimary: {
        backgroundColor: FINANCE.primary,
        boxShadow: FINANCE.shadowSm,
        '&:hover': { backgroundColor: FINANCE.primaryDark, boxShadow: FINANCE.shadowMd },
      },
      outlined: {
        borderColor: FINANCE.lineDark,
        color: FINANCE.text,
        '&:hover': {
          borderColor: FINANCE.primary,
          backgroundColor: FINANCE.primaryLight,
          color: FINANCE.primaryDark,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: FINANCE.radiusSm,
        backgroundColor: FINANCE.surface,
        '& fieldset': { borderColor: FINANCE.lineDark },
        '&:hover fieldset': { borderColor: FINANCE.muted },
        '&.Mui-focused fieldset': {
          borderColor: FINANCE.primary,
          borderWidth: 2,
        },
      },
    },
    MuiInputLabel: {
      root: {
        color: FINANCE.muted,
        fontSize: '0.8125rem',
        '&.Mui-focused': { color: FINANCE.primary },
      },
    },
    MuiTableCell: {
      head: {
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.02em',
        textTransform: 'none',
        color: FINANCE.text,
        backgroundColor: FINANCE.tableHead,
        borderBottom: `1px solid ${FINANCE.lineDark}`,
        padding: '10px 16px',
      },
      body: {
        borderBottom: `1px solid ${FINANCE.line}`,
        fontSize: '0.8125rem',
        padding: '8px 16px',
      },
    },
    MuiTableRow: {
      root: {
        '&:hover': { backgroundColor: FINANCE.primaryLight },
      },
    },
    MuiPaper: {
      rounded: { borderRadius: FINANCE.radius },
      elevation1: { boxShadow: FINANCE.shadowSm },
      elevation2: { boxShadow: FINANCE.shadowMd },
    },
    MuiChip: {
      root: { borderRadius: FINANCE.radiusSm },
    },
    MuiTab: {
      root: {
        minWidth: 88,
        fontWeight: 500,
        fontSize: '0.8125rem',
        textTransform: 'none',
        color: FINANCE.muted,
        '&.Mui-selected': { color: FINANCE.primary, fontWeight: 600 },
      },
    },
    MuiTabs: {
      indicator: { backgroundColor: FINANCE.primary, height: 3, borderRadius: '3px 3px 0 0' },
    },
    MuiSelect: {
      root: { borderRadius: FINANCE.radiusSm },
    },
  },
});
